import { mergeStyleSets } from '@fluentui/react';
import {
  CloseIcon,
  Dialog,
  Dropdown,
  Flex,
  ProviderConsumer as FluentUIThemeConsumer,
  RadioGroup,
  Text
} from '@fluentui/react-northstar';
import { Input } from 'adaptivecards';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appState, useSetState } from "../AppState";
import MgtCss from '../mgt.module.css';
import { searchUsers } from '../shared/cache/PlatformUserCache';
import { FileProps } from '../shared/components/EditTicketAttachments/EditTicketAttachments';
import { NewAttachmentFlowContent } from '../shared/components/EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent';
import { TicketCustomFormSection } from '../shared/components/TicketCustomFormTab/TicketCustomFormSection';
import { CustomFormUploadProps, mapCardInputToAnswers, parseCard, updateRequestValue } from '../shared/components/TicketTemplateAnswer';
import { TEMPLATE } from '../shared/utils/constants';
import { AdaptiveCardElement } from './AdaptiveCardElement';
import { RequestTitle } from './CustomComponents/RequestTitle';
import { UploadFile, UploadFileProps } from './CustomComponents/UploadFile';
import { SEND_FORM_OPTION } from './CustomForms/constants';
import { CustomFormAttachmentsProps } from './CustomForms/CustomForm.Interface';
import { parseCardTemplate, updateActiveFormState } from './CustomForms/CustomForm.Service';
import { TicketTemplatesSelectionProperties } from './TicketTemplatesSelection';

const classNames = mergeStyleSets({
  customFormDialog: {
    '.ac-textBlock': {
      padding: '5px 0 !important'
    }
  },
  userDropDown:{
    border: "1px solid"
  },
  dropdownInDialog: {
    // Force the dropdown list to use fixed positioning so it escapes overflow containers
    '.ui-dropdown__items-list': {
      position: 'fixed !important' as any,
      zIndex: 99999
    }
  }
});

export const TicketTemplatesSelectionComplete = (
  props: TicketTemplatesSelectionProperties
) => {
  const { t } = useTranslation();
  const currentState = appState();
  const setContextState = useSetState();
  const {ticketData, dispatchEvent, dataSourceList, affectedUserList, collaboratorList} = props;
  const [selectedOption, setSelectedOption] = useState(currentState.answerCustomFormNow ? 'answer' : 'send');
  const pageKey = "TemplateSelect";
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadFiles] = useState<File[]>([]);
  const [affectedUsers, setAffectedUsers] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [showAnswerForm, setShowAnswerForm] = useState(false);
  const [selectedAffectedUserId, setSelectedAffectedUserId] = useState("");
  const [selectedCollaboratorId, setSelectedCollaboratorId] = useState("");
  const [customForms, setCustomForms] = useState(currentState.customFormState.customForms ?? []);
  const [activeCustomForm, setActiveCustomForm] = useState(currentState.customFormState.activeForm);
  const [customFormAttachmentsProps, setCustomFormAttachmentsProps] = useState<CustomFormAttachmentsProps>({} as CustomFormAttachmentsProps);
  const [customFormListAttachmentsProps, setCustomFormListAttachmentsProps] = useState<CustomFormAttachmentsProps[]>([]);
  const [resetAnswerContainer, setResetAnswerContainer] = useState(false);
  
  const answerJSON = JSON.parse('{}');
  const cardPayload =
    ticketData['SelectedTemplate'] &&
    ticketData['SelectedTemplate']['CardResponseJson']
      ? JSON.parse(ticketData['SelectedTemplate']['CardResponseJson'])
      : null;
  if (cardPayload){ 
    parseCard(cardPayload.body, answerJSON, currentState, {}, dataSourceList);
    ticketData['SelectedTemplate']['CardResponseJson'] = JSON.stringify(cardPayload);
  }
  const [_, setCardAnswerJson] = useState(answerJSON);
  ticketData['SelectedTemplate']['CardAnswerJson'] = JSON.stringify(answerJSON);

  if (ticketData['SelectedTemplate']['ClearFormAnswer']===undefined) 
    ticketData['SelectedTemplate']['ClearFormAnswer'] = true;

  if (ticketData['SelectedTemplate']['SendCustomFormSettings']===undefined){ 
    ticketData['SelectedTemplate']['SendCustomFormSettings'] = {
      Type: SEND_FORM_OPTION.SEND_TO_REQUESTER, 
      SelectedAffectedUserId: "",
      SelectedCollaboratorId: ""
    };
  }


  const onInputValueChanged = (input: Input) => {
    let thisCardAnswerJson = activeCustomForm.CardAnswerJson;
    let inputId = mapCardInputToAnswers(input, thisCardAnswerJson);

    if (cardPayload && inputId !== RequestTitle.ElementId){ 
      updateRequestValue(cardPayload.body, thisCardAnswerJson, currentState);
    }

    //save to old custom form 
    ticketData['SelectedTemplate']['CardResponseJson'] = JSON.stringify(activeCustomForm.CardTemplateJson);
    ticketData['SelectedTemplate']['CardAnswerJson'] = JSON.stringify(thisCardAnswerJson);
    ticketData['SelectedTemplate']['ClearFormAnswer'] = false;
  };

  const getSendOptions = (globalTheme: any) =>{
    const chooseItems = [
      {
        name: 'templateType',
        key: 'SendForm',
        label: TEMPLATE.SEND_FORM,
        value: SEND_FORM_OPTION.SEND_TO_REQUESTER
      },
      {
        name: 'templateType',
        key: 'SendFormToAffectedUser',
        label: TEMPLATE.SEND_FORM_AFFECTEDUSER,
        value: SEND_FORM_OPTION.SEND_TO_AFFECTEDUSER,
        disabled: (affectedUserList?.length ?? 0) == 0,
        children: (Component, { key, ...props }) => {
          return (
            <Flex key={key} column className={classNames.dropdownInDialog}>
              <Component {...props} />
              <Dropdown 
                {...(selectedOption != SEND_FORM_OPTION.SEND_TO_AFFECTEDUSER && {
                      className: MgtCss.disabledContainer
                    })}
                items={affectedUsers} 
                placeholder={'Select Affected User'}
                style={{ background: globalTheme.siteVariables.colorScheme.default.background2, width:'fit-content', marginLeft: '30px' }}
                onChange={(e, p) => {setSelectedAffectedUserId(p.value["Id"]);}} />
            </Flex>
          )
        }
      },
      {
        name: 'templateType',
        key: 'SendFormToAssignee',
        label: TEMPLATE.SEND_FORM_ASSIGNEE,
        value: SEND_FORM_OPTION.SEND_TO_ASSIGNEE,
        disabled: (ticketData?.Id ?? 0) > 0 ? !ticketData?.AssigneeId : !ticketData?.["Entity"]?.["AssigneeId"]
      },
      {
        name: 'templateType',
        key: 'SendFormToCollaborator',
        label: TEMPLATE.SEND_FORM_COLLABORATOR,
        value: SEND_FORM_OPTION.SEND_TO_COLLABORATOR,
        disabled: (collaboratorList?.length ?? 0) == 0,
        children: (Component, { key, ...props }) => {
          return (
            <Flex key={key} column className={classNames.dropdownInDialog}>
              <Component {...props} />
              <Dropdown 
                {...(selectedOption != SEND_FORM_OPTION.SEND_TO_COLLABORATOR && {
                      className: MgtCss.disabledContainer
                    })}
                items={collaborators} 
                placeholder={'Select Collaborator'}
                style={{ background: globalTheme.siteVariables.colorScheme.default.background2, width:'fit-content', marginLeft: '30px' }}
                onChange={(e, p) => { setSelectedCollaboratorId(p.value["Id"]);}} />
            </Flex>
          )
        }
      }
    ];

    if(showAnswerForm){
      chooseItems.push({
        name: 'templateType',
        key: 'AnswerForm',
        label: `${ticketData?.Id > 0 ? TEMPLATE.ANSWER_FORM_APPLY : TEMPLATE.ANSWER_FORM_CREATE}`,
        value: SEND_FORM_OPTION.ANSWER_NOW
      });
    }
    return chooseItems;
  }

  useEffect(()=>{
    const sortedTemplateForms = [...customForms].sort((a, b) => a.Order - b.Order);
    setCustomForms(sortedTemplateForms);
    ticketData['SelectedTemplate']['CustomForms'] = customForms;

    if(sortedTemplateForms != null && sortedTemplateForms.length > 0){
      const initialForm = sortedTemplateForms[0];
      initialForm.IsAnswered = true;
      updateActiveFormState(initialForm, setContextState)  
    }
  },[]);

  useEffect(()=>{
    setCustomForms(currentState.customFormState.customForms);
  },[currentState.customFormState.customForms])

   useEffect(() => {
    const getAffectedUsers = async () => {
      try {
        const userList = [];
        affectedUserList?.forEach(async u => {
          const userId = u.PlatformUserId || u.Id;
          const user = await searchUsers({ Id: userId } as PlatformUser, true);
          userList.push({
            Id: user.Id,
            FullName: user.FullName,
            header: user.FullName,
            accessibilityitemprops: { id: user.Id },
            selected: false
          });
        });
        setAffectedUsers(userList);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    getAffectedUsers();
  }, [affectedUserList]); 

  useEffect(() => {
    const getCollaborators = async () => {
      try {
        const userList = [];
        collaboratorList?.forEach(async u => {
          const userId = u.PlatformUserId || u;
          const user = await searchUsers({ Id: userId } as PlatformUser, true)
          userList.push({
            Id: user.Id,
            FullName: user.FullName,
            header: user.FullName,
            accessibilityitemprops: { id: user.Id },
            selected: false
          });
        });
        setCollaborators(userList);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    getCollaborators();
  }, [collaboratorList]); 

  useEffect(()=>{
    switch(currentState.openSendFormDialogFrom){
      case "sendForm":
        setShowAnswerForm(false);
        break;
      case "applyTemplate":
      case "udpateAnswer":
      default:
        setShowAnswerForm(true);
        break;
    }
  },[currentState.openSendFormDialogFrom])

  useEffect(()=>{
    ticketData['SelectedTemplate']['SendCustomFormSettings'] = {
        Type: selectedOption, 
        SelectedAffectedUserId: selectedAffectedUserId,
        SelectedCollaboratorId: selectedCollaboratorId
    };
    dispatchEvent({ type: 'set-handle-customform', data: ticketData });
  },[selectedAffectedUserId, selectedCollaboratorId, selectedOption]);

  useEffect(()=>{
    if(activeCustomForm != undefined){
      const extraProps = getExtrasUploadProps(activeCustomForm);
      parseCard(activeCustomForm.CardTemplateJson.body, activeCustomForm.CardAnswerJson, currentState, extraProps, dataSourceList)
    }
  },[activeCustomForm])

  useEffect(()=>{
    setActiveCustomForm(currentState.customFormState.activeForm);
  },[currentState.customFormState.activeForm])

  useEffect(()=>{
    ticketData.SelectedTemplate.CustomForms = customForms;
  },[customForms])

  const getExtrasUploadProps = (thisActiveCustomForm) => {
    const extraProps: CustomFormUploadProps = {
      activeFormTemplateId: thisActiveCustomForm.TemplateFormId
    }

    if(!customFormAttachmentsProps?.TemplateFormId)
      extraProps.setCustomFormAttachmentsProps = setCustomFormAttachmentsProps;

    return extraProps;
  }

  useEffect(() => {
      if(customFormAttachmentsProps?.TemplateFormId && activeCustomForm)
        setCustomFormListAttachmentsProps(prev => {
          let idFound = false;
          let newItems = prev.map(item => {
            if(item.TemplateFormId === activeCustomForm.TemplateFormId){
              item = Object.assign(item, customFormAttachmentsProps);
              idFound = true;
            }
            return item;
          });
  
          if(!idFound){
            const newItem = {TemplateFormId: activeCustomForm.TemplateFormId, ...customFormAttachmentsProps } as CustomFormAttachmentsProps;
            newItems.push(newItem);
          }
          return newItems;
        });
  
    }, [customFormAttachmentsProps]);

  useEffect(() => {
    ticketData['SelectedTemplate']['CardFileAttachments'] = customFormListAttachmentsProps;
  }, [customFormListAttachmentsProps]);

  
  useEffect(() => {
    setResetAnswerContainer(false);
  }, [activeCustomForm?.CardAnswerJson]);

  useEffect(() => {
    if (!resetAnswerContainer) setResetAnswerContainer(true);
  },[resetAnswerContainer]);

  const onCloseUploadFromCardAction = (currentFiles?: FileProps[], optionalMessage?: string, isPrivate?: boolean) => {
    setOpenUploadDialog(false);
    if(currentFiles?.length > 0){
      let thisCardAnswerJson = activeCustomForm.CardAnswerJson;
      thisCardAnswerJson[UploadFile.ElementId] = JSON.stringify({Files: currentFiles.map(x => { return { FileName: x.FileName}; }), Comment: {Body: optionalMessage, IsPublic: !isPrivate}} as UploadFileProps);
      setCardAnswerJson(thisCardAnswerJson);
      //save to old custom form 
      ticketData['SelectedTemplate']['CardResponseJson'] = JSON.stringify(activeCustomForm.CardTemplateJson);
      ticketData['SelectedTemplate']['CardAnswerJson'] = JSON.stringify(thisCardAnswerJson);
      ticketData['SelectedTemplate']['ClearFormAnswer'] = false;

      if(ticketData.Id > 0){
        customForms.forEach(cf => {
          if(cf.TemplateFormId == activeCustomForm.TemplateFormId)
            cf.CardAnswerJson = thisCardAnswerJson;
          return cf;
        });
      }
      
      const newFormAttachmentProps: CustomFormAttachmentsProps = {TemplateFormId: activeCustomForm.TemplateFormId, Files: currentFiles, Message: optionalMessage, IsPrivate: isPrivate};
      setCustomFormAttachmentsProps(newFormAttachmentProps);
      dispatchEvent({ type: 'update-ticket', data: { CardFileAttachments: [newFormAttachmentProps] } });
    }
  }
  
  return (
    <FluentUIThemeConsumer
      render={globalTheme => {
        return (
          <>
            <Flex
              column
              className="max-h-full md:max-h-70vh overflow-hidden md:overflow-auto md:mb-3 mb-0"
              gap="gap.large"
            >
              <Flex.Item>
                <Flex column gap="gap.small">
                  <Text content={TEMPLATE.CHOOSE_FINAL_ACTION} weight="bold" />
                  <RadioGroup
                    vertical
                    items={getSendOptions(globalTheme)}
                    defaultCheckedValue={selectedOption}
                    onCheckedValueChange={(e, data) => {
                      const selectedValue = data['value'].toString();
                      setSelectedOption(selectedValue);
                      
                      if(selectedValue == SEND_FORM_OPTION.ANSWER_NOW){
                         ticketData['SelectedTemplate']['ClearFormAnswer'] = false;
                          setContextState (preState => ({
                            ...preState,
                            answerCustomFormNow: true
                          }));
                      }else{
                         ticketData['SelectedTemplate']['ClearFormAnswer'] = true;
                          setContextState(preState => ({
                            ...preState,
                            answerCustomFormNow: false
                          }));
                      }
                      dispatchEvent({ type: 'set-handle-customform', data: ticketData });
                    }}
                  />
                </Flex>
              </Flex.Item>
              
              {showAnswerForm && <Flex.Item>
                <Flex 
                  gap="gap.medium"
                  className="h-full md:h-45vh overflow-hidden md:overflow-auto flex-wrap w-full"
                  {...(selectedOption != SEND_FORM_OPTION.ANSWER_NOW && {
                    className: MgtCss.disabledContainer
                  })}
                >
                {(currentState.customFormState.isSummaryPage) ? 
                      <Flex column style={{width: '100%'}} gap='gap.medium'> 
                        <Flex column style={{width: '100%'}} gap='gap.medium'>
                          <TicketCustomFormSection customForms={customForms.filter(x=>x.IsAnswered)} disabledFormInputs={true} template={ticketData['SelectedTemplate']} showSummaryInfo={true}/>
                        </Flex>
                      </Flex> : 
                      <Flex column style={{width: '100%'}} gap='gap.medium'>
                        {resetAnswerContainer &&
                          <AdaptiveCardElement 
                            jsonString = {(activeCustomForm==undefined) ? "{}" : 
                              parseCardTemplate(activeCustomForm.CardTemplateJson, activeCustomForm.CardAnswerJson, currentState, dataSourceList, getExtrasUploadProps(activeCustomForm), " ")}
                            onInputValueChanged={onInputValueChanged}
                            cardProps={{
                              styles: { width: '100%', height: 'fit-content' }
                            }}
                            containerClassName={classNames.customFormDialog}
                            uploadFileProps={{key: pageKey, currentFiles: customFormAttachmentsProps?.Files, message: customFormAttachmentsProps?.Message, messageIsPrivate: customFormAttachmentsProps?.IsPrivate,
                              onOpenUploadFromCardAction: () =>{
                                setOpenUploadDialog(!openUploadDialog);
                              }
                            }}
                          />    
                        }
                      </Flex>}
                </Flex>
              </Flex.Item>}
            </Flex>
            <Dialog
              closeOnOutsideClick={false}
              open={openUploadDialog}
              content={
                <NewAttachmentFlowContent globalTheme={globalTheme} ticketId={ticketData?.Id ?? 0} filesToUpload={uploadFiles} onInteraction={() => {}} teamsConfig={currentState.teamsConfig} isEndUserPage={false} onClose={onCloseUploadFromCardAction} uploadFromCustomFormProps={{fromTemplate: true, attachToCustomFormFromWeb: true, isApplyTemplate: (ticketData?.Id ?? 0) > 0}} />
              }
              header={t('ticket-details.edit-ticket-form.custom-form-answer.addto-customform')}
              headerAction={{
                icon: <CloseIcon />,
                title: t('common.buttons.close'),
                onClick: () => setOpenUploadDialog(false)
              }}
              style={{ maxWidth: '700px' }}
              className="responsive-dialog top-0 fixed md:relative z-10 mobile-full-p md:w-attachment"
            />
          </>
        );
      }}
    />
  );
};

